// "Is typing" (CHAT-TYPING-1) — both halves of the hint.
//
// SEND: the composers call chatTypingPing on every input that leaves text in
// the field and chatTypingStop when the text is sent or cleared. A `start`
// goes out at most every 3 s per (channel, thread) — the server drops a repeat
// inside 2 s anyway — and a `stop` only follows a `start` this element sent.
// The server checks the caller may open the channel and stamps the identity
// from the token (POST /chat/channels/:id/typing → the `chat.typing` frame).
//
// RECEIVE: subscribeRealtime hands every `chat.typing` row to chatTypingApply,
// which returns the next root._chatTypists (NOT `chatTyping` — that root key
// is the AI assistant's "working" flag, read by its composer spinner):
//   { [channelId]: { [email]: { email, userId, thread, until } } }
// A `start` (re)arms an entry for 6 s, a `stop` drops it, and the person's
// next message in that channel drops it too (chatTypingClear, from the
// message-insert path). chatTypingPrune drops expired entries, so a lost
// `stop` (a closed tab, a dropped network) heals on its own.
//
// Each function is self-contained (frank serializes them one by one).
import { getSDK } from '@symbo-ls/workspace-shared/functions/sdk.js'

// The next root._chatTypists after one `chat.typing` row, or null when nothing
// changes. The viewer's own rows (another tab of theirs) are ignored.
export const chatTypingApply = (root, row, now) => {
  if (!root || !row || !row.channel_id) return null
  const email = String(row.user_email || '')
    .trim()
    .toLowerCase()
  const me = String(root.currentUserEmail || '')
    .trim()
    .toLowerCase()
  if (!email || email === me) return null
  const at = typeof now === 'number' ? now : Date.now()
  const all = root._chatTypists || {}
  const cid = String(row.channel_id)
  const inChannel = { ...(all[cid] || {}) }
  if (row.state === 'stop') {
    if (!inChannel[email]) return null
    delete inChannel[email]
  } else {
    inChannel[email] = {
      email,
      userId: row.user_id != null ? String(row.user_id) : null,
      thread: row.thread_parent_id ? String(row.thread_parent_id) : null,
      until: at + 6000
    }
  }
  const next = { ...all }
  if (Object.keys(inChannel).length) next[cid] = inChannel
  else delete next[cid]
  return next
}

// Drop one person's entry in a channel (their message arrived). Null when
// there is nothing to drop.
export const chatTypingClear = (root, channelId, email) => {
  const all = (root && root._chatTypists) || {}
  const cid = channelId != null ? String(channelId) : ''
  const key = String(email || '')
    .trim()
    .toLowerCase()
  if (!cid || !key || !all[cid] || !all[cid][key]) return null
  const inChannel = { ...all[cid] }
  delete inChannel[key]
  const next = { ...all }
  if (Object.keys(inChannel).length) next[cid] = inChannel
  else delete next[cid]
  return next
}

// Drop every expired entry. Null when nothing expired.
export const chatTypingPrune = (root, now) => {
  const all = (root && root._chatTypists) || {}
  const at = typeof now === 'number' ? now : Date.now()
  let changed = false
  const next = {}
  for (const cid of Object.keys(all)) {
    const inChannel = {}
    for (const key of Object.keys(all[cid] || {})) {
      const entry = all[cid][key]
      if (entry && entry.until > at) inChannel[key] = entry
      else changed = true
    }
    if (Object.keys(inChannel).length) next[cid] = inChannel
  }
  return changed ? next : null
}

// The display names typing in a channel right now — in the channel itself
// (thread null) or in one thread. `this` is the calling element. Names come
// from the chat people lookup (email, then the member-row bridge), then the
// /people row by user id (a non-manager's peers have no email), then the
// email's local part.
export const chatTypingNames = function (root, channelId, thread) {
  const r = root || (this && this.state && this.state.root)
  const all = (r && r._chatTypists) || {}
  const inChannel = channelId ? all[String(channelId)] : null
  if (!inChannel) return []
  const now = Date.now()
  const want = thread ? String(thread) : null
  const names = []
  for (const key of Object.keys(inChannel)) {
    const entry = inChannel[key]
    if (!entry || entry.until <= now || (entry.thread || null) !== want) continue
    let person = null
    try {
      person = this && typeof this.call === 'function' ? this.call('chatFindPerson', r, key) : null
    } catch (_) {
      person = null
    }
    if (!person && entry.userId) {
      person =
        (r.people || []).find(
          (p) => p && String(p.userId ?? p.user_id ?? '') === String(entry.userId)
        ) || null
    }
    names.push(String((person && person.name) || key.split('@')[0]))
  }
  return names.sort()
}

// The line under a conversation: "Ana is typing", "Ana and Bo are typing",
// "Several people are typing" — or '' when nobody is.
export const chatTypingLabel = function (channelId, thread) {
  const names = this.call('chatTypingNames', null, channelId, thread) || []
  if (!names.length) return ''
  if (names.length === 1)
    return this.call('polyglot', 'chat.typing.one').replace('{name}', names[0])
  if (names.length === 2) {
    return this.call('polyglot', 'chat.typing.two')
      .replace('{a}', names[0])
      .replace('{b}', names[1])
  }
  return this.call('polyglot', 'chat.typing.many')
}

// Tell the conversation the viewer is typing. At most one `start` per 3 s for
// the same (channel, thread); `this` (the composer's field) remembers it. An
// SDK without the route, or a server without it (404), switches the hint off
// for this field instead of erroring on every keystroke.
export const chatTypingPing = function (channelId, thread) {
  if (!channelId || !this || this.__typingOff) return
  const now = Date.now()
  const last = this.__typingSent
  const threadId = thread || null
  if (last && last.channelId === channelId && last.thread === threadId && now - last.at < 3000) {
    return
  }
  this.__typingSent = { channelId, thread: threadId, at: now }
  const el = this
  Promise.resolve()
    .then(() =>
      getSDK().execute('workspaceProject.chat', 'typing', {
        channelId,
        state: 'start',
        threadParentId: threadId || undefined
      })
    )
    .catch((err) => {
      const msg = String((err && err.message) || '')
      if (/does not support op|not found|404/i.test(msg) || (err && err.status === 404)) {
        el.__typingOff = true
      }
    })
}

// The viewer stopped (sent, cleared the field, switched conversation). Ends
// the `start` this field last sent, if it is still live; a no-op otherwise.
export const chatTypingStop = function () {
  const last = this && this.__typingSent
  if (!last) return
  this.__typingSent = null
  if (this.__typingOff || Date.now() - last.at > 8000) return
  Promise.resolve()
    .then(() =>
      getSDK().execute('workspaceProject.chat', 'typing', {
        channelId: last.channelId,
        state: 'stop',
        threadParentId: last.thread || undefined
      })
    )
    .catch(() => {})
}
